import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { TimeSelect } from '@/components/ui/time-select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useEnsureLocalCalendar } from '@/hooks/useCalendar';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

const ACTIVITY_TYPES = ['in_person', 'phone_call', 'video_call', 'email', 'sms'] as const;

const DURATION_PRESETS = [
  { label: '15m', minutes: 15 },
  { label: '30m', minutes: 30 },
  { label: '1hr', minutes: 60 },
] as const;

const activitySchema = z.object({
  type: z.enum(ACTIVITY_TYPES, {
    required_error: "Activity type is required",
  }),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional().nullable(),
  start_date: z.date({
    required_error: "Date is required",
  }),
  start_time: z.string().default(''),
  duration: z.number().optional().nullable(),
  location: z.string().optional().nullable(),
  addToCalendar: z.boolean().default(false),
});

type ActivityFormData = z.infer<typeof activitySchema>;

interface ActivityDefaultValues {
  title?: string;
  type?: typeof ACTIVITY_TYPES[number];
  start_date?: Date;
  description?: string;
  duration?: number;
}

interface CreateActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  onSuccess?: () => void;
  activity?: {
    id: string;
    type: 'in_person' | 'phone_call' | 'video_call' | 'email' | 'sms';
    title: string;
    description?: string;
    start_date: string;
    end_date?: string;
    location?: string;
  } | null;
  defaultValues?: ActivityDefaultValues;
}

function computeEndDate(startDate: Date, startTime: string, durationMinutes: number): Date {
  const end = new Date(startDate);
  if (startTime) {
    const [hours, minutes] = startTime.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes)) {
      end.setHours(hours, minutes, 0, 0);
    }
  }
  end.setMinutes(end.getMinutes() + durationMinutes);
  return end;
}

function buildStartDateTime(date: Date, time: string): Date {
  const result = new Date(date);
  if (time) {
    const [hours, minutes] = time.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes)) {
      result.setHours(hours, minutes, 0, 0);
    }
  }
  return result;
}

export function CreateActivityDialog({
  open,
  onOpenChange,
  contactId,
  onSuccess,
  activity,
  defaultValues: prefill,
}: CreateActivityDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [customDuration, setCustomDuration] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const ensureLocalCalendar = useEnsureLocalCalendar();

  const now = new Date();
  const defaultTime = prefill?.start_date
    ? format(prefill.start_date, 'HH:mm')
    : format(now, 'HH:mm');

  const form = useForm<ActivityFormData>({
    resolver: zodResolver(activitySchema),
    defaultValues: {
      type: prefill?.type || 'phone_call',
      title: prefill?.title || '',
      description: prefill?.description || '',
      start_date: prefill?.start_date || now,
      start_time: defaultTime,
      duration: prefill?.duration || undefined,
      location: '',
      addToCalendar: false,
    },
  });

  const selectedDuration = form.watch('duration');

  // Only reset form when the dialog opens
  useEffect(() => {
    if (open) {
      const currentTime = new Date();
      if (activity) {
        const activityDate = new Date(activity.start_date);
        let durationMins: number | undefined;
        if (activity.end_date) {
          const end = new Date(activity.end_date);
          durationMins = Math.round((end.getTime() - activityDate.getTime()) / 60000);
          if (durationMins <= 0) durationMins = undefined;
        }
        form.reset({
          type: activity.type,
          title: activity.title,
          description: activity.description || '',
          start_date: activityDate,
          start_time: format(activityDate, 'HH:mm'),
          duration: durationMins,
          location: activity.location || '',
          addToCalendar: false,
        });
        if (durationMins && ![15, 30, 60].includes(durationMins)) {
          setCustomDuration(String(durationMins));
        } else {
          setCustomDuration('');
        }
      } else {
        const startDate = prefill?.start_date || currentTime;
        form.reset({
          type: prefill?.type || 'phone_call',
          title: prefill?.title || '',
          description: prefill?.description || '',
          start_date: startDate,
          start_time: format(startDate, 'HH:mm'),
          duration: prefill?.duration || undefined,
          location: '',
          addToCalendar: false,
        });
        setCustomDuration('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity, form]);

  const handleDurationSelect = (minutes: number) => {
    const current = form.getValues('duration');
    if (current === minutes) {
      form.setValue('duration', undefined);
      setCustomDuration('');
    } else {
      form.setValue('duration', minutes);
      setCustomDuration('');
    }
  };

  const handleCustomDuration = (value: string) => {
    setCustomDuration(value);
    const mins = parseInt(value, 10);
    if (!isNaN(mins) && mins > 0) {
      form.setValue('duration', mins);
    } else {
      form.setValue('duration', undefined);
    }
  };

  const isCustomSelected = selectedDuration != null
    && ![15, 30, 60].includes(selectedDuration);

  const onSubmit = async (data: ActivityFormData) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data: contact } = await supabase
        .from('contacts')
        .select('workspace_id')
        .eq('id', contactId)
        .single();

      const startDateTime = buildStartDateTime(data.start_date, data.start_time);
      const endDateTime = data.duration
        ? computeEndDate(data.start_date, data.start_time, data.duration)
        : null;
      // Add-to-calendar is an explicit forward-looking intent signal — force
      // the activity to scheduled regardless of time, so all-day events and
      // same-minute saves don't silently land as completed.
      const isScheduled = data.addToCalendar || startDateTime > new Date();

      const cleanData = {
        type: data.type,
        title: data.title,
        description: data.description || null,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime?.toISOString() || null,
        location: data.location || null,
        scheduled: isScheduled,
        // Past-dated activity (and no calendar checkbox) = logged after fact = done.
        // Future-dated, or any addToCalendar = scheduled to-do; user marks complete later.
        completed: !isScheduled,
      };

      let createdActivityId: string | null = null;

      if (activity) {
        const { error } = await supabase
          .from('activities')
          .update(cleanData)
          .eq('id', activity.id);

        if (error) throw error;
        createdActivityId = activity.id;
        toast({ description: "Activity updated successfully" });
      } else {
        const { data: insertedActivity, error } = await supabase
          .from('activities')
          .insert({
            ...cleanData,
            contact_id: contactId,
            workspace_id: contact?.workspace_id,
            user_id: user.id,
          })
          .select('id')
          .single();

        if (error) throw error;
        createdActivityId = insertedActivity?.id ?? null;
        toast({ description: "Activity created successfully" });
      }

      // Add to calendar (works for both create and edit)
      if (data.addToCalendar) {
        try {
          const { data: calendars } = await supabase
            .from('calendar_calendars')
            .select('id, provider_id')
            .eq('workspace_id', contact?.workspace_id)
            .eq('user_id', user.id)
            .eq('is_selected', true)
            .limit(1);

          const selectedCalendar = calendars?.[0];
          let calendarId = selectedCalendar?.id;

          if (!calendarId) {
            const local = await ensureLocalCalendar(contact?.workspace_id);
            if (!local) throw new Error('Could not create local calendar');
            calendarId = local.id;
          }

          if (calendarId) {
            // Check for existing calendar event to avoid duplicates
            const { data: existing } = await supabase
              .from('calendar_events')
              .select('id')
              .eq('calendar_id', calendarId)
              .eq('title', data.title)
              .eq('start_time', startDateTime.toISOString())
              .limit(1);

            let calendarEventId: string | null = null;

            if (existing && existing.length > 0) {
              calendarEventId = existing[0].id;
            } else {
              const isAllDay = !data.duration;
              const { data: newEvent, error: eventErr } = await supabase
                .from('calendar_events')
                .insert({
                  calendar_id: calendarId,
                  workspace_id: contact?.workspace_id,
                  user_id: user.id,
                  title: data.title,
                  description: data.description || null,
                  start_time: startDateTime.toISOString(),
                  end_time: endDateTime?.toISOString() || null,
                  all_day: isAllDay,
                  source: 'local',
                })
                .select('id')
                .single();

              if (eventErr) throw eventErr;
              calendarEventId = newEvent?.id ?? null;
            }

            if (calendarEventId && createdActivityId) {
              const { error: linkErr } = await supabase
                .from('activities')
                .update({ calendar_event_id: calendarEventId })
                .eq('id', createdActivityId);

              if (linkErr) {
                console.error('Failed to link activity to calendar event:', linkErr);
                captureError(linkErr as Error, { feature: 'link-activity-calendar-event' });
              }
            }
          }
        } catch (calError) {
          console.error('Failed to create calendar event:', calError);
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.activities.all });

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      captureError(error as Error, { feature: activity ? 'activity-update' : 'activity-create' });
      toast({
        title: "Error",
        description: `Failed to ${activity ? 'update' : 'create'} activity`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{activity ? 'Edit Activity' : 'Create Activity'}</DialogTitle>
          <DialogDescription>
            {activity ? 'Update activity details' : 'Log a past activity or schedule a future one'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

            {/* Type + Title row */}
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="phone_call">Phone Call</SelectItem>
                        <SelectItem value="in_person">In Person</SelectItem>
                        <SelectItem value="video_call">Video Call</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Activity title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add details about this activity"
                      className="resize-none"
                      rows={2}
                      value={value || ''}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date + Time + Duration row */}
            <div>
              <FormLabel className="mb-2 block">When</FormLabel>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Date picker */}
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal w-[160px]",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, "MMM d, yyyy") : "Pick date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => date && field.onChange(date)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                />

                {/* Time input */}
                <FormField
                  control={form.control}
                  name="start_time"
                  render={({ field }) => (
                    <TimeSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />

                {/* Duration pills */}
                <div className="flex items-center gap-1">
                  {DURATION_PRESETS.map((preset) => (
                    <Button
                      key={preset.minutes}
                      type="button"
                      variant={selectedDuration === preset.minutes ? "default" : "outline"}
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={() => handleDurationSelect(preset.minutes)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant={isCustomSelected ? "default" : "outline"}
                    size="sm"
                    className="h-8 px-3 text-xs"
                    onClick={() => {
                      if (isCustomSelected) {
                        form.setValue('duration', undefined);
                        setCustomDuration('');
                      } else {
                        setCustomDuration(customDuration || '45');
                        form.setValue('duration', parseInt(customDuration || '45', 10));
                      }
                    }}
                  >
                    Custom
                  </Button>
                  {isCustomSelected && (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={1}
                        className="w-[60px] h-8 text-xs"
                        value={customDuration}
                        onChange={(e) => handleCustomDuration(e.target.value)}
                        placeholder="min"
                      />
                      <span className="text-xs text-muted-foreground">min</span>
                    </div>
                  )}
                </div>
              </div>
              <FormField
                control={form.control}
                name="start_date"
                render={() => <FormMessage />}
              />
            </div>

            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field: { value, ...field } }) => (
                <FormItem>
                  <FormLabel>Location (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Activity location"
                      value={value || ''}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Add to calendar */}
            <FormField
              control={form.control}
              name="addToCalendar"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-sm cursor-pointer">
                    Add to calendar
                    {field.value && !selectedDuration && (
                      <span className="text-xs text-muted-foreground ml-1">(all-day event)</span>
                    )}
                  </FormLabel>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isLoading}>
                {isLoading
                  ? (activity ? "Updating..." : "Saving...")
                  : (activity ? "Update Activity" : "Save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
